/**
 * The plan as an attack path.
 *
 * Five fixed columns, left to right: scope, recon, surface, hypothesis,
 * objective. Fixed rather than free-form because a fixed pipeline is a reading
 * order and a free-form graph is a picture that needs a legend. The Lead is
 * told the same five, so what it emits and what this draws are the same shape.
 *
 * Laid out here rather than by a graph library: with the column fixed by the
 * node's own `column` field, position is just "which column, and how many are
 * already in it". A force layout would move boxes between renders and make the
 * same plan look different twice, which is the opposite of what an approval
 * screen needs.
 *
 * WHY THE EDGES CANNOT DRIFT. Every path is drawn from the same `layout()`
 * numbers the boxes are drawn from, so geometry has exactly one source. A
 * mockup of this screen faked the paths at fixed coordinates while the boxes
 * flowed, and the lines ended up in the gaps connecting nothing, which is worth
 * recording because an animated line that connects nothing is the whole reason
 * motion on a screen like this gets called noise.
 *
 * COLOUR DOES TWO JOBS AND NEVER BOTH AT ONCE. The lane hue tells you where you
 * are left to right and lives on the left rail of the box. The state colour
 * says what is happening to that step and takes the border, so it always wins.
 *
 * MOTION IS ONLY EVER STATE. Two things move: the dot beside a codename while
 * that agent's run is unparked, and the edges along a path an agent is on right
 * now. Nothing else animates, so a still board means nothing is running, which
 * is a thing worth being able to see from across a room.
 *
 * Excluded nodes stay on the canvas, greyed. Removing them would hide what the
 * operator decided against, and that decision is part of the record.
 */

const COLUMNS = [
  { key: 'scope', label: 'Scope', hue: '#6366f1' },
  { key: 'recon', label: 'Recon it will run', hue: '#0891b2' },
  { key: 'surface', label: 'Surface it expects', hue: '#7c3aed' },
  { key: 'hypothesis', label: 'Angles', hue: '#48b94c' },
  { key: 'objective', label: 'What it would establish', hue: '#ea580c' },
];

// Bigger, because these cards are the plan and they were reading as labels.
// A 25-character truncation turned every hypothesis into "Catchall-masked path
// exp..." which is the part that says nothing; the distinguishing words are
// always at the end of a title like that.
const COL_W = 296;
const NODE_W = 260;
const NODE_H = 104;
const ROW_GAP = 20;
const TOP = 14;
const RAIL = 3;

//: What is happening to a step. `neutral` is the honest default: a node with no
//: agent and no verdict is not "waiting", it is simply a step in a plan.
const STATE = {
  live: { stroke: '#16a34a', ring: 'rgba(22,163,74,.16)', text: '#0f172a' },
  done: { stroke: '#0891b2', ring: 'transparent', text: '#0f172a' },
  refuted: { stroke: '#7c3aed', ring: 'transparent', text: '#64748b' },
  failed: { stroke: '#e11d48', ring: 'rgba(225,29,72,.12)', text: '#0f172a' },
  gate: { stroke: '#d97706', ring: 'transparent', text: '#0f172a' },
  neutral: { stroke: '#e2e8f0', ring: 'transparent', text: '#0f172a' },
  off: { stroke: '#e2e8f0', ring: 'transparent', text: '#94a3b8' },
};

const EDGE = {
  live: { stroke: '#16a34a', width: 2, className: 'ap-flow' },
  refuted: { stroke: '#c4b5fd', width: 1.25, dash: '3 4' },
  done: { stroke: '#bae6fd', width: 1.4 },
  neutral: { stroke: '#cbd5e1', width: 1.25 },
};

export function layout(nodes) {
  const perColumn = {};
  const placed = [];
  (nodes || []).forEach((node) => {
    const index = Math.max(0, COLUMNS.findIndex((c) => c.key === node.column));
    const row = perColumn[index] || 0;
    perColumn[index] = row + 1;
    placed.push({
      ...node,
      column_index: index,
      x: index * COL_W + (COL_W - NODE_W) / 2,
      y: TOP + row * (NODE_H + ROW_GAP),
    });
  });
  const tallest = Math.max(1, ...Object.values(perColumn));
  return { placed, height: TOP * 2 + tallest * (NODE_H + ROW_GAP) };
}

/**
 * What state a node is in, and who is on it.
 *
 * Derived from the rows rather than stored on the node: a plan is the intent an
 * operator approved, and writing today's progress into it would blur what was
 * agreed with what happened. Resolved through the node's `ref`, which is the
 * only durable link a box has to a real row.
 */
export function nodeState(node, {
  anglesById, runsByAngle, excluded, reconByNode,
}) {
  if (excluded?.has(node.id)) return { state: 'off' };

  // RECON NODES ARE RESOLVED FROM THE RECON LOG, NOT FROM AN ANGLE.
  //
  // A recon step is a scanner the Lead dispatches on its own authority (see
  // recon.py): it carries `tool` and no `ref` into a hypothesis, so every
  // lookup below would miss it and leave it neutral whether it ran, finished
  // or failed. `reconByNode` is the outcome log keyed on the same node id the
  // plan already carries; no invented linkage. Checked first and returned
  // early, because a node cannot be both a recon step and an angle.
  if (node.tool) {
    const row = reconByNode?.[node.id];
    if (row?.status === 'dispatched') return { state: 'live', beat: true };
    if (row?.status === 'completed') return { state: 'done' };
    if (row?.status === 'failed') return { state: 'failed' };
    // Skipped, or no row at all (recon has not been dispatched yet): a step
    // that never ran is a step in a plan, the same honest default as below.
    return { state: 'neutral' };
  }

  const ref = node.ref || '';
  const angleId = ref.startsWith('hypothesis:') ? Number(ref.split(':')[1]) : null;
  const angle = angleId ? anglesById?.[angleId] : null;
  const run = angleId ? runsByAngle?.[angleId] : null;

  // A failed run is louder than the angle's own status: an angle sitting
  // `investigating` behind a run that died is the case an operator most needs
  // to see, and it is exactly the one a status-only reading hides.
  if (run && run.status === 'failed') {
    return { state: 'failed', who: run.display_name, turns: run, beat: false };
  }
  if (angle) {
    if (angle.status === 'refuted' || angle.status === 'abandoned') {
      return { state: 'refuted', who: run?.display_name, strike: true };
    }
    if (angle.status === 'confirmed') return { state: 'done', who: run?.display_name };
    if (angle.status === 'investigating') {
      const beat = !!run && (run.status === 'running' || run.status === 'refuting');
      return { state: 'live', who: run?.display_name, turns: run, beat };
    }
  }
  if (node.level_required && node.level_required !== 'confirm') return { state: 'gate' };
  return { state: 'neutral' };
}

function edgeState(from, to, states) {
  const a = states[from]?.state;
  const b = states[to]?.state;
  if (a === 'off' || b === 'off') return 'neutral';
  if (a === 'live' || b === 'live') return 'live';
  if (a === 'refuted' || b === 'refuted') return 'refuted';
  if (a === 'done' || b === 'done') return 'done';
  return 'neutral';
}

function truncate(text, max) {
  const value = text || '';
  return value.length > max ? `${value.slice(0, max - 1)}\u2026` : value;
}

/**
 * Break a title across up to `lines` rows of about `max` characters.
 *
 * SVG has no text wrapping, so this is done by hand and rendered as tspans.
 * Breaks on a space so a word is never split, and only truncates the LAST
 * line, which is what keeps the distinguishing end of a title visible instead
 * of chopping it at 25 characters.
 */
function wrap(text, max, lines) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const out = [];
  let current = '';
  for (const w of words) {
    const next = current ? `${current} ${w}` : w;
    if (next.length <= max) { current = next; continue; }
    out.push(current);
    current = w;
    if (out.length === lines - 1) break;
  }
  if (out.length < lines) out.push(current);
  const rest = words.slice(out.join(' ').split(/\s+/).filter(Boolean).length);
  if (rest.length) out[out.length - 1] = truncate(`${out[out.length - 1]} ${rest.join(' ')}`, max);
  return out.filter(Boolean).slice(0, lines);
}

export default function AttackPath({
  nodes, edges, excluded, selectedId, onSelect, angles, roster, recon,
}) {
  const { placed, height } = layout(nodes);
  const byId = Object.fromEntries(placed.map((n) => [n.id, n]));
  const width = COLUMNS.length * COL_W;

  const anglesById = Object.fromEntries((angles || []).map((a) => [a.id, a]));
  const runsByAngle = Object.fromEntries(
    (roster || []).filter((m) => m.hypothesis_id).map((m) => [m.hypothesis_id, m]),
  );
  // The recon log, keyed on the node id it was dispatched from. See
  // recon.py's `entries`: {node, tool, status, target, task_id}.
  const reconByNode = Object.fromEntries((recon || []).map((r) => [r.node, r]));

  const states = Object.fromEntries(
    placed.map((n) => [n.id, nodeState(n, {
      anglesById, runsByAngle, excluded, reconByNode,
    })]),
  );
  const anyLive = Object.values(states).some((s) => s.state === 'live');

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <div className="grid border-b border-slate-100 bg-slate-50/60"
           style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, ${COL_W}px)`, width }}>
        {COLUMNS.map((column, i) => (
          <div key={column.key}
               className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider"
               style={{ color: column.hue }}>
            <span className="grid h-4 w-4 place-items-center rounded-[5px] text-[9px] font-bold text-white"
                  style={{ background: column.hue }}>
              {i + 1}
            </span>
            {column.label}
          </div>
        ))}
      </div>

      <svg width={width} height={height} role="img" aria-label="Planned attack path">
        <defs>
          {/* Scoped to this svg rather than the global sheet: the animation is
              part of the drawing, and a keyframe defined somewhere else is a
              keyframe somebody deletes without seeing what stops moving.
              Suspended entirely when nothing is live, so an idle board holds
              still instead of animating at nothing. */}
          <style>{`
            @keyframes apFlow { to { stroke-dashoffset: -24; } }
            @keyframes apBeat { 0%,100% { opacity: 1 } 50% { opacity: .25 } }
            .ap-flow { stroke-dasharray: 5 7; animation: apFlow 1.15s linear infinite; }
            .ap-beat { animation: apBeat 1.5s ease-in-out infinite; }
            @media (prefers-reduced-motion: reduce) {
              .ap-flow, .ap-beat { animation: none; }
              .ap-flow { stroke-dasharray: 5 7; }
            }
          `}</style>
          {Object.entries(EDGE).map(([key, tone]) => (
            <marker key={key} id={`ap-arrow-${key}`} viewBox="0 0 10 10" refX="9" refY="5"
                    markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" fill={tone.stroke} />
            </marker>
          ))}
        </defs>

        {COLUMNS.slice(1).map((c, i) => (
          <line key={c.key} x1={(i + 1) * COL_W} y1={0} x2={(i + 1) * COL_W} y2={height}
                stroke="#f1f5f9" strokeWidth="1" />
        ))}

        {/* Edges first so a box always sits on top of its own lines. */}
        {(edges || []).map((edge, i) => {
          const from = byId[edge.source];
          const to = byId[edge.target];
          if (!from || !to) return null;
          const kind = edgeState(edge.source, edge.target, states);
          const tone = edge.gate ? { stroke: '#d97706', width: 2 } : EDGE[kind];
          const x1 = from.x + NODE_W;
          const y1 = from.y + NODE_H / 2;
          const x2 = to.x;
          const y2 = to.y + NODE_H / 2;
          const mid = (x1 + x2) / 2;
          const dim = excluded.has(edge.source) || excluded.has(edge.target);
          return (
            <g key={`${edge.source}-${edge.target}-${i}`} opacity={dim ? 0.25 : 1}>
              <path
                className={anyLive && kind === 'live' && !edge.gate ? 'ap-flow' : undefined}
                d={`M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`}
                fill="none"
                stroke={tone.stroke}
                strokeWidth={tone.width}
                strokeDasharray={tone.dash}
                markerEnd={`url(#ap-arrow-${edge.gate ? 'live' : kind})`}
              />
              {edge.gate && (
                <g>
                  <circle cx={mid} cy={(y1 + y2) / 2} r="9" fill="#fffbeb" stroke="#fcd34d" />
                  <text x={mid} y={(y1 + y2) / 2 + 3.5} textAnchor="middle"
                        fontSize="9" fontWeight="700" fill="#92400e">
                    {edge.gate[0].toUpperCase()}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {placed.map((node) => {
          const info = states[node.id];
          const off = info.state === 'off';
          const tone = STATE[info.state] || STATE.neutral;
          const selected = node.id === selectedId;
          const hue = COLUMNS[node.column_index]?.hue || '#94a3b8';
          const chip = info.who;
          return (
            <g key={node.id} onClick={() => onSelect(node.id)} style={{ cursor: 'pointer' }}>
              {tone.ring !== 'transparent' && (
                <rect x={node.x - 2.5} y={node.y - 2.5} width={NODE_W + 5} height={NODE_H + 5}
                      rx="10.5" fill="none" stroke={tone.ring} strokeWidth="3" />
              )}
              <rect
                x={node.x} y={node.y} width={NODE_W} height={NODE_H} rx="8"
                fill="#ffffff"
                stroke={selected ? '#48b94c' : tone.stroke}
                strokeWidth={selected ? 2 : 1}
                strokeDasharray={off ? '4 3' : undefined}
              />
              {/* The lane rail. Clipped to the box's own corner radius by
                  drawing it as a rounded rect the box then overlaps. */}
              {!off && (
                <path
                  d={`M ${node.x + 8} ${node.y} h -${8 - RAIL} a 8 8 0 0 0 -${RAIL} ${RAIL}
                      v ${NODE_H - RAIL * 2} a 8 8 0 0 0 ${RAIL} ${RAIL} h ${8 - RAIL} z`}
                  fill={info.state === 'neutral' || info.state === 'gate' ? hue : tone.stroke}
                />
              )}

              <text x={node.x + 14} y={node.y + 21} fontSize="13" fontWeight="700"
                    fill={tone.text}
                    style={{ textDecoration: (off || info.strike) ? 'line-through' : 'none' }}>
                {wrap(node.label, 30, 2).map((line, li) => (
                  <tspan key={li} x={node.x + 14} dy={li === 0 ? 0 : 15}>{line}</tspan>
                ))}
              </text>

              {/* What it points at, then what it rests on. Both, where there
                  used to be one or the other: `ref` says which row this box IS
                  and `based_on` says what justified it, and "2 citation(s)"
                  told the operator neither. */}
              <text x={node.x + 14} y={node.y + 60} fontSize="10"
                    fill={off ? '#cbd5e1' : '#94a3b8'} fontFamily="ui-monospace, monospace">
                {off ? 'excluded' : (node.tool || node.ref || '')}
              </text>
              {!off && (node.based_on || []).length > 0 && (
                <text x={node.x + 14} y={node.y + 73} fontSize="9.5"
                      fill="#b0b8c4" fontFamily="ui-monospace, monospace">
                  {truncate(`from ${(node.based_on || []).join(' ')}`, 34)}
                </text>
              )}

              {node.level_required && node.level_required !== 'confirm' && !off && (
                <text x={node.x + NODE_W - 12} y={node.y + 19} fontSize="10" fontWeight="700"
                      textAnchor="end" fill="#b45309">
                  {node.level_required.toUpperCase()}
                </text>
              )}

              {/* Who is on it, and whether their run is unparked. The dot is the
                  only thing on this board tied to something that changes second
                  to second, and it sits beside the name it describes. */}
              {chip ? (
                <g>
                  <rect x={node.x + 12} y={node.y + NODE_H - 26} rx="9"
                        width={Math.min(NODE_W - 24, 11 + chip.length * 5.6 + (info.beat ? 14 : 4) + 26)}
                        height="18" fill="#f8fafc" stroke={tone.stroke} opacity="0.95" />
                  {info.beat && (
                    <circle className="ap-beat" cx={node.x + 22} cy={node.y + NODE_H - 17}
                            r="3" fill={tone.stroke} />
                  )}
                  <text x={node.x + (info.beat ? 30 : 20)} y={node.y + NODE_H - 13.5}
                        fontSize="9.5" fontWeight="600" fill={tone.stroke}
                        fontFamily="ui-monospace, monospace">
                    {truncate(chip, 18)}
                    {info.turns ? ` · ${info.turns.turns_used ?? ''}` : ''}
                  </text>
                </g>
              ) : info.beat && (
                // A RECON STEP HAS NO "WHO". `chip` comes from an angle's
                // agent name, and a scanner running under the Lead's own
                // authority has none, so the name chip above would render as
                // an empty rounded rect with nothing in it. The ring already
                // says this box is live; the beat just needs to sit on its
                // own rather than inside a label for a name that does not
                // exist.
                <circle className="ap-beat" cx={node.x + 22} cy={node.y + NODE_H - 17}
                        r="3" fill={tone.stroke} />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export { COLUMNS, STATE };
